import {
  UsersPage,
  TermsPage,
  SignInPage,
  BillingPage,
  CamerasPage,
  InvoicePage,
  Error403Page,
  CustomerPage,
  ProfilesPage,
  LogReportPage,
  UserGuidePage,
  VerticalsPage,
  EquipmentsPage,
  UserGroupsPage,
  SpiaReportPage,
  ConfirmUserPage,
  RestrictionsPage,
  EquipmentMapPage,
  IntegrationsPage,
  CamerasMosaicPage,
  CaptureReportPage,
  CreateAccountPage,
  CustomizationPage,
  FilterCamerasPage,
  FlowDashboardPage,
  AverageSpeedPage,
  ResetPasswordPage,
  CloneDashboardPage,
  EditUserGroupsPage,
  IncidentReportPage,
  CameraDashboardPage,
  ForensicsReportPage,
  ConvoyDashboardPage,
  VehicleOverviewPage,
  MonitoringReportPage,
  ForensicsReportViewer,
  MonitoringVehiclesPage,
  RestrictionDashboardPage,
  InformationProtectionPage,
  EditUserGroupsPermissionPage,
  MonitoredVehiclesDashboardPage
} from "pages";
import { FC } from "react";
import Pages from "enums/Pages";
import history from "utils/history";
import { useAuth } from "contexts/AuthContext";
import { Router as RRouter } from "react-router";
import { canRead } from "utils/PermissionManager";
import { Redirect, Route, Switch } from "react-router-dom";
import ConvoyReportPage from "pages/ConvoyReport/ConvoyReportPage";
import MonitoredRoutesPage from "pages/Settings/MonitoredRoutes/MonitoredRoutesPage";
import SharedDataPage from "pages/Settings/SharedData/SharedDataPage";
import FleetDashboardPage from "pages/FleetDashboard/FleetDashboardPage";

type RouteRedirect = [boolean, string]; // Redirect to <string> if <boolean> is true

type ProtectedRouteProps = {
  exact?: boolean;
  path: string;
  search?: string;
  hash?: string;
  redirects: RouteRedirect[];
  onReject?: () => void; // Optional function that executes if rejected
};

/**
 * Creates a route that renders a redirect if the reject condition is met.
 */
const ProtectedRoute: FC<ProtectedRouteProps> = ({
  exact = false,
  path,
  search,
  hash,
  redirects = [],
  onReject,
  children
}) => (
  <Route
    exact={exact}
    path={path}
    render={({ location }) => {
      for (const redirect of redirects) {
        if (redirect[0]) {
          if (onReject) onReject();
          return (
            <Redirect
              to={{
                pathname: redirect[1],
                search,
                hash,
                state: { from: location }
              }}
            />
          );
        }
      }
      return children;
    }}
  />
);

export const HOME_PAGE = Pages.EQUIPMENT_MAP;

/**
 * Aggregator for all routes
 */
const Router: FC = () => {
  const { sessionUser } = useAuth();

  const isSignedIn = sessionUser !== null;
  const isTermsAccepted = sessionUser?.isTermsAccepted ?? false;

  const signedInRedirects: RouteRedirect[] = [[isSignedIn, HOME_PAGE]];
  const protectedRedirects = (page?: string): RouteRedirect[] => {
    const arr: RouteRedirect[] = [
      [!isSignedIn, Pages.SIGN_IN],
      [!isTermsAccepted, Pages.TERMS]
    ];

    if (page) {
      const canReadPage = canRead(page, sessionUser);
      arr.push([!canReadPage, Pages.ERROR_403]);
    }
    return arr;
  };

  return (
    <RRouter history={history}>
      <Switch>
        <ProtectedRoute
          exact
          path="/"
          redirects={[
            [!isSignedIn, Pages.SIGN_IN],
            [!isTermsAccepted, Pages.TERMS],
            [true, HOME_PAGE] // Always trigger this redirection if the ones above don't
          ]}
        />
        <ProtectedRoute
          exact
          path={Pages.SIGN_IN}
          redirects={signedInRedirects}
        >
          <SignInPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.CREATE_ACCOUNT}
          redirects={signedInRedirects}
        >
          <CreateAccountPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.RESET_PASSWORD}
          redirects={signedInRedirects}
        >
          <ResetPasswordPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.CONFIRM_USER}
          redirects={signedInRedirects}
        >
          <ConfirmUserPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.TERMS}
          redirects={[
            [!isSignedIn, Pages.SIGN_IN],
            [isTermsAccepted, HOME_PAGE]
          ]}
        >
          <TermsPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.USERS}
          redirects={protectedRedirects("USERS")}
        >
          <UsersPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.USER_GROUPS}
          redirects={protectedRedirects("USER_GROUPS")}
        >
          <UserGroupsPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={`${Pages.USER_GROUPS}/:name`}
          redirects={protectedRedirects("USER_GROUPS")}
        >
          <EditUserGroupsPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={`${Pages.USER_GROUPS}/:name/:permission`}
          redirects={protectedRedirects("USER_GROUPS")}
        >
          <EditUserGroupsPermissionPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.PROFILES}
          redirects={protectedRedirects("PROFILES")}
        >
          <ProfilesPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.SHARED_DATA}
          redirects={protectedRedirects("SHARED_DATA")}
        >
          <SharedDataPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.CUSTOMERS}
          redirects={protectedRedirects("CUSTOMERS")}
        >
          <CustomerPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.VERTICALS}
          redirects={protectedRedirects("VERTICALS")}
        >
          <VerticalsPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.EQUIPMENTS}
          redirects={protectedRedirects("EQUIPMENTS")}
        >
          <EquipmentsPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.CAMERAS}
          redirects={protectedRedirects("CAMERAS")}
        >
          <CamerasPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.MONITORING_VEHICLES_DASHBOARD}
          redirects={protectedRedirects("MONITORING_VEHICLES_DASHBOARD")}
        >
          <MonitoredVehiclesDashboardPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.CAMERA_DASHBOARD}
          redirects={protectedRedirects("CAMERA_DASHBOARD")}
        >
          <CameraDashboardPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.FLOW_DASHBOARD}
          redirects={protectedRedirects("FLOW_DASHBOARD")}
        >
          <FlowDashboardPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.AVERAGE_SPEED}
          redirects={protectedRedirects("AVERAGE_SPEED")}
        >
          <AverageSpeedPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.RESTRICTION_DASHBOARD}
          redirects={protectedRedirects("RESTRICTION_DASHBOARD")}
        >
          <RestrictionDashboardPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.CLONE_DASHBOARD}
          redirects={protectedRedirects("CLONE_DASHBOARD")}
        >
          <CloneDashboardPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.CONVOY_DASHBOARD}
          redirects={protectedRedirects("CONVOY_DASHBOARD")}
        >
          <ConvoyDashboardPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.USER_GUIDE}
          redirects={protectedRedirects("USER_GUIDE")}
        >
          <UserGuidePage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.MOSAIC}
          redirects={protectedRedirects("MOSAIC")}
        >
          <CamerasMosaicPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.SYSTEM_FILTER_CAMERAS}
          redirects={protectedRedirects("SYSTEM_FILTER_CAMERAS")}
        >
          <FilterCamerasPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.MONITORING_VEHICLES}/:plate/:class/:brand/:model/:color`}
          redirects={protectedRedirects("MONITORING_VEHICLES")}
        >
          <MonitoringVehiclesPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.MONITORING_VEHICLES}/:plate/:class`}
          redirects={protectedRedirects("MONITORING_VEHICLES")}
        >
          <MonitoringVehiclesPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.MONITORING_VEHICLES}/:plate`}
          redirects={protectedRedirects("MONITORING_VEHICLES")}
        >
          <MonitoringVehiclesPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.MONITORING_VEHICLES}
          redirects={protectedRedirects("MONITORING_VEHICLES")}
        >
          <MonitoringVehiclesPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.VEHICLE_OVERVIEW}/:plate`}
          redirects={protectedRedirects("VEHICLE_OVERVIEW")}
        >
          <VehicleOverviewPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.RESTRICTION}/:rule`}
          redirects={protectedRedirects("RESTRICTION")}
        >
          <RestrictionsPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.RESTRICTION}
          redirects={protectedRedirects("RESTRICTION")}
        >
          <RestrictionsPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.MONITORED_ROUTES}
          redirects={protectedRedirects("MONITORED_ROUTES")}
        >
          <MonitoredRoutesPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.CAPTURE_REPORT}/:plate`}
          redirects={protectedRedirects("CAPTURE_REPORT")}
        >
          <CaptureReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.CAPTURE_REPORT}
          redirects={protectedRedirects("CAPTURE_REPORT")}
        >
          <CaptureReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.CONVOY_REPORT}
          redirects={protectedRedirects("CONVOY_REPORT")}
        >
          <ConvoyReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.FORENSIC_REPORT}/:report`}
          redirects={protectedRedirects("FORENSIC_REPORT")}
        >
          <ForensicsReportViewer />
        </ProtectedRoute>
        <ProtectedRoute
          path={`${Pages.INCIDENT_REPORT}/:plate`}
          redirects={protectedRedirects("INCIDENT_REPORT")}
        >
          <IncidentReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.INCIDENT_REPORT}
          redirects={protectedRedirects("INCIDENT_REPORT")}
        >
          <IncidentReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.FORENSIC_REPORT}
          redirects={protectedRedirects("FORENSIC_REPORT")}
        >
          <ForensicsReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.LOG_REPORT}
          redirects={protectedRedirects("LOG_REPORT")}
        >
          <LogReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.MONITORING_REPORT}
          redirects={protectedRedirects("MONITORING_REPORT")}
        >
          <MonitoringReportPage />
        </ProtectedRoute>
        {/* <ProtectedRoute
          path={Pages.CORRELATION_REPORT}
          redirects={protectedRedirects("CORRELATION_REPORT")}
        >
          <CorrelationReportPage />
        </ProtectedRoute> */}
        <ProtectedRoute
          path={Pages.SPIA_REPORT}
          redirects={protectedRedirects("SPIA_REPORT")}
        >
          <SpiaReportPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.EQUIPMENT_MAP}
          redirects={protectedRedirects("EQUIPMENT_MAP")}
        >
          <EquipmentMapPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.BILLING}
          redirects={protectedRedirects("BILLING")}
        >
          <BillingPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.INVOICE}
          redirects={protectedRedirects("INVOICE")}
        >
          <InvoicePage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.CUSTOMIZATION}
          redirects={protectedRedirects("CUSTOMIZATION")}
        >
          <CustomizationPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.INFORMATION_PROTECTION}
          redirects={protectedRedirects("INFORMATION_PROTECTION")}
        >
          <InformationProtectionPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.INTEGRATIONS}
          redirects={protectedRedirects("INTEGRATIONS")}
        >
          <IntegrationsPage />
        </ProtectedRoute>
        <ProtectedRoute
          path={Pages.FLEET}
          redirects={protectedRedirects("FLEET")}
        >
          <FleetDashboardPage />
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path={Pages.ERROR_403}
          redirects={protectedRedirects()}
        >
          <Error403Page />
        </ProtectedRoute>
      </Switch>
    </RRouter>
  );
};

export default Router;
